// src/pages/StrategyDetailPage.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom'; // Добавили useLocation
import { useTranslation } from 'react-i18next';
import { HiArrowLeft, HiExclamationCircle } from 'react-icons/hi';
import { useStrategyWebSocket } from '../hooks/useStrategyWebSocket';
import { getStrategyById, getStrategies, getRecommendedStrategies } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';

import StrategySettings from '../components/strategies/StrategySettings';
import StrategyManager from '../components/strategies/StrategyManager';
import StrategyChartComponent from '../components/strategies/StrategyChartComponent';
import StrategySummary from '../components/strategies/StrategySummary';
import StrategyOrderbooksPanel from '../components/strategies/StrategyOrderbooksPanel';
import TradesTable from '../components/strategies/TradesTable';
import EquityChart from '../components/strategies/EquityChart';

// Значения по умолчанию для НОВОЙ стратегии
const defaultNewStrategyParams = {
  symbol_a: '', symbol_b: '', timeframe: '5', zscore_period: '300', bar: '30000',
  margin: '100', leverage: '1', total_usdt_one_entry: 100, commission_rate: '0.1', exit: '0',
  levels: { short: [2, 3, 4], long: [-2, -3, -4] } 
};

/**
 * Нормализация данных из БД для компонента настроек.
 */
const normalizeStrategyData = (dbData) => {
    if (!dbData) return defaultNewStrategyParams;
    let p = dbData.parameters || dbData.params || {};
    if (typeof p === 'string') {
        try { p = JSON.parse(p); } catch (e) { console.error(e); }
    }

    return {
        symbol_a: p.symbol_a || '',
        symbol_b: p.symbol_b || '',
        timeframe: String(p.timeframe || '5'),
        zscore_period: p.zscore_period !== undefined ? String(p.zscore_period) : '300',
        bar: p.bar || p.bar_count || '30000',
        margin: p.margin !== undefined && p.margin !== '' ? String(p.margin) : '100',
        leverage: p.leverage !== undefined && p.leverage !== '' ? String(p.leverage) : '1',
        strategy_method: p.strategy_method || 'zscore',
        total_usdt_one_entry: Number(p.total_usdt_one_entry || 100),
        commission_rate: p.commission_rate !== undefined ? String(p.commission_rate * 100) : '0.1',
        exit: p.exit !== undefined ? String(p.exit) : '0',
        level_entry: {
            level_entry_positive: p.level_entry_positive || [2, 3, 4],
            level_entry_negative: p.level_entry_negative || [-2, -3, -4]
        },
        levels: { 
            short: p.level_entry_positive || [2, 3, 4], 
            long: p.level_entry_negative || [-2, -3, -4] 
        }
    };
};

const StrategyDetailPage = () => {
  const { strategyId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation(); // Читаем URL
  
  const [activeParams, setActiveParams] = useState(null);
  const [loadedParams, setLoadedParams] = useState(defaultNewStrategyParams);
  const [strategyTitle, setStrategyTitle] = useState('');
  const [isLoadingStrategy, setIsLoadingStrategy] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [summaryHeight, setSummaryHeight] = useState(null);
  const summaryRef = useRef(null);

  useEffect(() => {
    const fetchStrategy = async () => {
      const query = new URLSearchParams(location.search);

      // 1. ПРОВЕРКА: ПЕРЕХОД ИЗ АНАЛИЗА ПАР
      if (query.get('from_analysis') === 'true') {
        const analysisData = {
            symbol_a: query.get('symbol_a') || '',
            symbol_b: query.get('symbol_b') || '',
            timeframe: query.get('timeframe') || '5',
            zscore_period: query.get('zscore') || '300',
            bar: query.get('bar') || '30000',
            margin: query.get('margin') || '100',
            leverage: query.get('leverage') || '1',
            total_usdt_one_entry: Number(query.get('margin') || 100) * Number(query.get('leverage') || 1),
            commission_rate: query.get('comm') || '0.1',
            exit: query.get('exit') || '0',
            levels: {
                short: query.get('spos')?.split(',').map(Number) || [2, 3, 4],
                long: query.get('sneg')?.split(',').map(Number) || [-2, -3, -4]
            }
        };

        setLoadedParams(analysisData);
        setStrategyTitle(`${analysisData.symbol_a} / ${analysisData.symbol_b} (Analysis)`);
        
        // МГНОВЕННЫЙ ЗАПУСК СИМУЛЯЦИИ
        setActiveParams({
            ...analysisData,
            strategy_name: "pairs_trading",
            strategy_method: "zscore",
            commission_rate: (parseFloat(analysisData.commission_rate) || 0.1) / 100,
            zscore_period: Number(analysisData.zscore_period),
            bar: Number(analysisData.bar),
            exit: Number(analysisData.exit),
            level_entry: {
                level_entry_positive: analysisData.levels.short,
                level_entry_negative: analysisData.levels.long
            }
        });
        
        setIsLoadingStrategy(false);
        return;
      }

      // 2. СТАНДАРТНАЯ ЛОГИКА (НОВАЯ СТРАТЕГИЯ)
      if (strategyId === 'new') {
        setLoadedParams(defaultNewStrategyParams);
        setActiveParams(null);
        setStrategyTitle(t('detail.new_strategy'));
        setIsLoadingStrategy(false);
        return;
      }

      if (!strategyId || strategyId === 'undefined') {
        setFetchError(t('detail.error_invalid_id'));
        setIsLoadingStrategy(false);
        return;
      }
      
      if (!user?.login) return;

      setIsLoadingStrategy(true);
      setFetchError(null);
      
      try {
        let strategyData = null;
        try {
            strategyData = await getStrategyById(strategyId, user.login);
        } catch (e) {
            const all = await getStrategies(user.login);
            strategyData = all.find(s => 
                String(s.publicStrategyId) === strategyId || 
                String(s.strategyId) === strategyId || 
                String(s._id) === strategyId
            );
        }

        if (!strategyData) {
            const recommended = await getRecommendedStrategies();
            strategyData = recommended.find(s => String(s.publicStrategyId) === strategyId);
        }
        
        if (strategyData) {
            const normalized = normalizeStrategyData(strategyData);
            setLoadedParams(normalized);
            
            // АВТОЗАПУСК СИМУЛЯЦИИ ДЛЯ СОХРАНЕННОЙ
            setActiveParams({
                ...normalized,
                strategy_name: "pairs_trading",
                strategy_method: "zscore",
                commission_rate: (parseFloat(normalized.commission_rate) || 0.1) / 100,
                zscore_period: Number(normalized.zscore_period),
                bar: Number(normalized.bar),
                exit: Number(normalized.exit)
            });

            setStrategyTitle(strategyData.customStrategyName || t('detail.page_title'));
        } else {
            throw new Error(t('detail.error_not_found'));
        }
      } catch (error) {
        console.error(error);
        setFetchError(error.message || t('detail.error_not_found'));
      } finally {
        setIsLoadingStrategy(false);
      }
    };

    fetchStrategy();
  }, [strategyId, user, t, location.search]);

  const { data, status } = useStrategyWebSocket(activeParams);

  useEffect(() => {
    if (!data || !summaryRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = Math.round(summaryRef.current?.getBoundingClientRect().height || 0);
      setSummaryHeight(nextHeight || null);
    };

    updateHeight();

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(summaryRef.current);

    return () => observer.disconnect();
  }, [data]);

  if (fetchError) {
      return (
        <div className="flex flex-col h-screen items-center justify-center text-white space-y-4">
            <HiExclamationCircle className="w-16 h-16 text-rose-500" />
            <h2 className="text-2xl font-bold">{t('detail.error_title')}</h2>
            <p className="text-slate-400">{fetchError}</p>
            <Link to="/strategies" className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-500 transition-colors">
                {t('detail.back_btn')}
            </Link>
        </div>
      );
  }

  if (isLoadingStrategy) {
      return <div className="flex h-screen items-center justify-center text-white font-bold animate-pulse uppercase tracking-widest">Loading Analytics...</div>;
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center space-x-4">
        <Link to="/strategies" className="p-2 text-gray-400 rounded-full hover:bg-slate-700 hover:text-white">
          <HiArrowLeft className="h-6 w-6" />
        </Link>
        <h1 className="text-3xl font-bold text-white">{strategyTitle}</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col min-h-[600px]">
          {activeParams ? (
            <div className="flex-1 w-full bg-slate-800 rounded-xl overflow-hidden relative flex flex-col border border-slate-700/50">
              <div className="flex-1 h-full w-full">
                  <StrategyChartComponent strategyParams={activeParams} data={data} status={status} className="h-full w-full" />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-800 rounded-xl text-gray-400 border-2 border-dashed border-slate-700 h-full">
              <p>{t('detail.placeholder')}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6 flex flex-col">
          <StrategySettings initialSettings={loadedParams} onApply={setActiveParams} />
          <StrategyManager activeParams={activeParams} strategyId={strategyId === 'new' ? null : strategyId} />
        </div>
      </div>

      {activeParams ? (
        <StrategyOrderbooksPanel
          strategyParams={activeParams}
          isVisible={Boolean(data)}
        />
      ) : null}

      {data && (
        <div className="space-y-6 animate-fade-in-up">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <EquityChart
              completedTrades={data.completedTrades_raw || []}
              cardHeight={summaryHeight}
            />
            <div ref={summaryRef}>
              <StrategySummary
                summary={data.summary}
                completedTrades={data.completedTrades_raw || []}
                strategyParams={activeParams}
              />
            </div>
          </div>
          <div className="space-y-6">
            <TradesTable 
                title={t('detail.open_trades')} 
                trades={data.openTrade?.entries || []} 
                type="open" 
                strategyParams={activeParams}
                strategyName={strategyTitle}
            />
            <TradesTable 
              title={t('detail.history')} 
              trades={data.completedTrades_raw || []} 
              type="completed" 
              strategyParams={activeParams}
              strategyName={strategyTitle}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyDetailPage;
